import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { doc, getDoc } from "firebase/firestore";
import { getDownloadURL } from "firebase/storage";
import { getType as mimeLookup } from "mime";
import { storageReference, projectLastModified } from "@config/firestore";
import { assoc, dropLast, isNil, map, pipe, prop, propOr, reduce, reject } from "ramda";
export function textOrBinary(filename) {
    var textFiles = [".csd", ".sco", ".orc", ".udo", ".txt", ".md", ".inc"];
    var lowerName = filename.toLowerCase();
    if (textFiles.some(function (extension) {
        return lowerName.endsWith(extension);
    })) {
        return "txt";
    }
    return "bin";
}
export function isAudioFile(fileName) {
    // currently does not deal with FLAC, not sure if browser supports it
    var mimeType = mimeLookup(fileName) || "";
    var endings = [".wav", ".ogg", ".mp3", "aiff", "flac"];
    var lower = fileName.toLowerCase();
    return endings.some(function (ending) {
        return lower.endsWith(ending);
    }) || mimeType.startsWith("audio");
}
export function isPlotDataFile(fileName) {
    return fileName.endsWith(".plot.csv") || fileName.endsWith(".plot.tsv");
}
export var generateEmptyDocument = function generateEmptyDocument(documentUid, filename) {
    return {
        filename: filename,
        currentValue: "",
        created: Date.now(),
        documentUid: documentUid,
        lastModified: Date.now(),
        savedValue: "",
        type: "txt",
        userUid: "",
        isModifiedLocally: false,
        path: []
    };
};
export var addDocumentToEMFS = /*#__PURE__*/ function () {
    var _ref = _asyncToGenerator(/*#__PURE__*/ _regeneratorRuntime.mark(function _callee(projectUid, csound, document, absolutePath) {
        var steps, path, downloadUrl, response, arrayBuffer, blob, encoder;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
            while (1)
                switch (_context.prev = _context.next) {
                    case 0:
                        if (document.filename) {
                            _context.next = 2;
                            break;
                        }
                        return _context.abrupt("return");
                    case 2:
                        if (!(document.type === "folder")) {
                            _context.next = 5;
                            break;
                        }
                        csound.fs.mkdir(document.filename);
                        return _context.abrupt("return");
                    case 5:
                        steps = absolutePath.split("/").filter(function (p) {
                            return p.length > 0;
                        });
                        if (steps.length > 1) {
                            csound.fs.mkdir(dropLast(1, steps).join("/"));
                        }
                        if (!(document.type === "bin")) {
                            _context.next = 33;
                            break;
                        }
                        path = "".concat(document.userUid, "/").concat(projectUid, "/").concat(document.documentUid);
                        _context.prev = 9;
                        _context.t0 = getDownloadURL;
                        _context.next = 13;
                        return storageReference(path);
                    case 13:
                        _context.t1 = _context.sent;
                        _context.next = 16;
                        return (0, _context.t0)(_context.t1);
                    case 16:
                        downloadUrl = _context.sent;
                        _context.next = 19;
                        return fetch(downloadUrl);
                    case 19:
                        response = _context.sent;
                        _context.next = 22;
                        return response.arrayBuffer();
                    case 22:
                        arrayBuffer = _context.sent;
                        blob = new Uint8Array(arrayBuffer);
                        _context.next = 26;
                        return csound.fs.writeFile(absolutePath, blob);
                    case 26:
                        _context.next = 31;
                        break;
                    case 28:
                        _context.prev = 28;
                        _context.t2 = _context["catch"](9);
                        console.error(_context.t2);
                    case 31:
                        _context.next = 38;
                        break;
                    case 33:
                        encoder = new TextEncoder();
                        _context.t3 = csound && typeof csound.fs.writeFile === "function";
                        if (!_context.t3) {
                            _context.next = 38;
                            break;
                        }
                        _context.next = 38;
                        return csound.fs.writeFile(absolutePath, encoder.encode(document.currentValue));
                    case 38:
                    case "end":
                        return _context.stop();
                }
        }, _callee, null, [[9, 28]]);
    }));
    return function addDocumentToEMFS(_x, _x2, _x3, _x4) {
        return _ref.apply(this, arguments);
    };
}();
export var fileDocumentDataToDocumentType = function fileDocumentDataToDocumentType(documentData) {
    var _documentData$created, _documentData$created2, _documentData$lastMod, _documentData$lastMod2;
    return {
        created: (_documentData$created = documentData === null || documentData === void 0 ? void 0 : (_documentData$created2 = documentData.created) === null || _documentData$created2 === void 0 ? void 0 : _documentData$created2.toMillis()) !== null && _documentData$created !== void 0 ? _documentData$created : undefined,
        currentValue: documentData["value"],
        description: documentData["description"],
        documentUid: documentData["documentUid"],
        filename: documentData["name"],
        isModifiedLocally: false,
        lastModified: (_documentData$lastMod = documentData === null || documentData === void 0 ? void 0 : (_documentData$lastMod2 = documentData.lastModified) === null || _documentData$lastMod2 === void 0 ? void 0 : _documentData$lastMod2.toMillis()) !== null && _documentData$lastMod !== void 0 ? _documentData$lastMod : undefined,
        savedValue: documentData["value"],
        type: documentData["type"],
        userUid: documentData["userUid"],
        path: reject(isNil, documentData["path"] || [])
    };
};
export var convertDocumentSnapToDocumentsMap = function convertDocumentSnapToDocumentsMap(documentsToAdd) {
    return pipe(map(prop("doc")), map(function (d) {
        return assoc("documentUid", d.id, d.data());
    }), reduce(function (accumulator, documentData) {
        accumulator[documentData["documentUid"]] = fileDocumentDataToDocumentType(documentData);
        return accumulator;
    }, {}))(documentsToAdd);
};
export var firestoreProjectToIProject = function firestoreProjectToIProject(project) {
    return {
        projectUid: project.id || "",
        description: propOr("", "description", project),
        documents: {},
        isPublic: propOr(false, "public", project),
        name: propOr("", "name", project),
        userUid: propOr("", "userUid", project),
        iconBackgroundColor: prop("iconBackgroundColor", project),
        iconForegroundColor: prop("iconForegroundColor", project),
        iconName: prop("iconName", project),
        tags: [],
        stars: {}
    };
};
export var convertProjectSnapToProject = /*#__PURE__*/ function () {
    var _ref2 = _asyncToGenerator(/*#__PURE__*/ _regeneratorRuntime.mark(function _callee2(projSnap) {
        var projData, lastModified, lastModifiedData, project;
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
            while (1)
                switch (_context2.prev = _context2.next) {
                    case 0:
                        projData = projSnap.data();
                        _context2.next = 3;
                        return getDoc(doc(projectLastModified, projSnap.id));
                    case 3:
                        lastModified = _context2.sent;
                        lastModifiedData = lastModified.exists() && lastModified.data();
                        project = firestoreProjectToIProject(projData);
                        project["projectUid"] = projSnap.id;
                        if (lastModifiedData && lastModifiedData.target) {
                            project["cachedProjectLastModified"] = lastModifiedData.target.toMillis();
                        }
                        if (projData.created) {
                            project["created"] = projData.created.toMillis();
                        }
                        return _context2.abrupt("return", project);
                    case 10:
                    case "end":
                        return _context2.stop();
                }
        }, _callee2);
    }));
    return function convertProjectSnapToProject(_x5) {
        return _ref2.apply(this, arguments);
    };
}();
